import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  FileText,
  Scale,
  AlertCircle,
  Shield,
  Gavel,
  UserCheck,
} from "lucide-react";

const sections = [
  {
    id: "acceptance",
    icon: UserCheck,
    title: "Acceptance of Terms",
    content: `By accessing and using Softlume's website and services, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.

These terms apply to all users of the site, including browsers, customers, and contributors.`,
  },
  {
    id: "services",
    icon: FileText,
    title: "Our Services",
    content: `Softlume provides software development, IT consulting, and related technology services. Our services include but are not limited to:

• Custom software development
• Web and mobile application development
• Cloud solutions and infrastructure
• IT consulting and strategy
• Cybersecurity services
• Maintenance and support

Specific service terms will be outlined in individual project agreements.`,
  },
  {
    id: "user-responsibilities",
    icon: Shield,
    title: "User Responsibilities",
    content: `When using our services, you agree to:

• Provide accurate and complete information
• Maintain the confidentiality of your account credentials
• Notify us immediately of any unauthorized access
• Use our services only for lawful purposes
• Not interfere with or disrupt the services
• Not attempt to gain unauthorized access to our systems
• Respect intellectual property rights
• Comply with all applicable laws and regulations`,
  },
  {
    id: "intellectual-property",
    icon: Scale,
    title: "Intellectual Property Rights",
    content: `All content, features, and functionality on our website are owned by Softlume and protected by international copyright, trademark, and other intellectual property laws.

Your Rights:
• Custom code developed specifically for you under a project agreement
• Rights explicitly granted in your service agreement

Our Rights:
• Pre-existing code, frameworks, and tools
• Website content, design, and branding
• Documentation and methodologies
• Any work not explicitly transferred to you

You may not copy, modify, distribute, sell, or lease any part of our services without explicit written permission.`,
  },
  {
    id: "project-agreements",
    icon: FileText,
    title: "Project Agreements",
    content: `Individual projects are governed by separate project agreements that outline:

• Scope of work and deliverables
• Timeline and milestones
• Payment terms and schedule
• Intellectual property ownership
• Confidentiality provisions
• Warranties and limitations
• Support and maintenance terms

In case of conflict between these Terms and a project agreement, the project agreement prevails for that specific project.`,
  },
  {
    id: "payment",
    icon: Gavel,
    title: "Payment Terms",
    content: `Payment terms are specified in individual project agreements. General terms include:

• Invoices are due within 30 days unless otherwise specified
• Late payments may incur interest charges
• We reserve the right to suspend services for non-payment
• All fees are non-refundable unless specified otherwise
• Prices are subject to change with 30 days notice
• Additional work outside the original scope will be billed separately`,
  },
  {
    id: "warranties",
    icon: Shield,
    title: "Warranties and Disclaimers",
    content: `Limited Warranty:
We warrant that our services will be performed in a professional manner consistent with industry standards.

Disclaimer:
OUR SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.

We do not warrant that:
• Services will be uninterrupted or error-free
• Defects will be corrected immediately
• Services will meet all your requirements
• Results obtained will be accurate or reliable`,
  },
  {
    id: "limitation-liability",
    icon: AlertCircle,
    title: "Limitation of Liability",
    content: `To the maximum extent permitted by law, Softlume shall not be liable for:

• Indirect, incidental, or consequential damages
• Loss of profits, revenue, or data
• Business interruption
• Any damages exceeding the amount paid to us in the 12 months preceding the claim

This limitation applies regardless of the theory of liability, whether contract, tort, negligence, or otherwise.

Some jurisdictions do not allow limitation of liability, so these limitations may not apply to you.`,
  },
  {
    id: "confidentiality",
    icon: Shield,
    title: "Confidentiality",
    content: `We respect the confidentiality of your information:

• We will not disclose your confidential information to third parties
• We implement appropriate security measures
• Our employees sign confidentiality agreements
• Specific confidentiality terms are included in project agreements

Exceptions:
• Information that becomes publicly available
• Information required to be disclosed by law
• Information disclosed with your permission`,
  },
  {
    id: "termination",
    icon: Gavel,
    title: "Termination",
    content: `Either party may terminate services:

• By mutual written agreement
• For material breach (with 30 days cure period)
• For non-payment (with 15 days notice)
• For convenience (with notice period specified in agreement)

Upon termination:
• All outstanding fees become immediately due
• You must cease using our services
• We will return or destroy confidential information
• Provisions that survive termination remain in effect`,
  },
  {
    id: "indemnification",
    icon: Shield,
    title: "Indemnification",
    content: `You agree to indemnify and hold Softlume harmless from any claims, damages, losses, or expenses (including legal fees) arising from:

• Your use of our services
• Your violation of these terms
• Your violation of any third-party rights
• Your breach of applicable laws or regulations`,
  },
  {
    id: "dispute-resolution",
    icon: Scale,
    title: "Dispute Resolution",
    content: `In case of disputes:

1. Negotiation: Parties will first attempt to resolve disputes through good-faith negotiation
2. Mediation: If negotiation fails, parties may pursue mediation
3. Arbitration: Unresolved disputes may be submitted to binding arbitration
4. Jurisdiction: Governed by the laws of India, with jurisdiction in New Delhi courts

Class Action Waiver:
You agree to resolve disputes individually and waive the right to participate in class actions.`,
  },
  {
    id: "modifications",
    icon: FileText,
    title: "Modifications to Terms",
    content: `We reserve the right to modify these terms at any time:

• Changes will be posted on this page
• Material changes will be notified via email
• Continued use constitutes acceptance of new terms
• You should review these terms periodically

If you disagree with changes, you must discontinue use of our services.`,
  },
  {
    id: "general",
    icon: Gavel,
    title: "General Provisions",
    content: `Entire Agreement: These terms, together with any project agreements, constitute the entire agreement between you and Softlume.

Severability: If any provision is found unenforceable, remaining provisions remain in effect.

Waiver: Failure to enforce any right does not waive that right.

Assignment: You may not assign rights without our written consent. We may assign our rights at any time.

Force Majeure: Neither party is liable for delays due to circumstances beyond reasonable control.

Notices: All notices must be in writing and sent to addresses specified in project agreements.`,
  },
  {
    id: "contact",
    icon: FileText,
    title: "Contact Information",
    content: `For questions about these Terms of Service, contact:

Softlume
Email: legal@softlume.in
Phone: +91 7827782844
Address: Ghitorni, New Delhi 110030, India

Business Hours: Monday - Saturday, 10:00 AM - 7:00 PM IST`,
  },
];

export default function TermsOfServicePage() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="terms-of-service-page"
    >
      {/* Hero */}
      <section
        ref={heroRef}
        className="relative min-h-[50vh] flex items-center overflow-hidden pt-20"
      >
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/95 to-slate-950" />
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 10, repeat: Infinity }}
            className="absolute top-1/3 right-1/3 w-96 h-96 bg-cyan-500/5 rounded-full blur-[150px]"
          />
        </motion.div>
        <motion.div
          style={{ opacity: heroOpacity }}
          className="relative max-w-7xl mx-auto px-6 md:px-12 py-20"
        >
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-4 block"
          >
            Legal
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight text-white mb-6"
          >
            Terms of <span className="glow-text">Service</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-base md:text-lg text-slate-400 max-w-3xl leading-relaxed"
          >
            Last Updated: February 27, 2026
          </motion.p>
        </motion.div>
      </section>

      {/* Content */}
      <section className="relative py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="space-y-12">
            {sections.map((section, index) => (
              <TermsSection key={section.id} section={section} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="relative py-16 bg-[#0a0f1e] border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-slate-400 text-sm">
            Have questions about our terms?{" "}
            <a
              href="mailto:legal@softlume.in"
              className="text-blue-400 hover:text-blue-300 underline"
            >
              Contact our legal team
            </a>
          </p>
        </div>
      </section>
    </motion.div>
  );
}

function TermsSection({ section, index }) {
  const ref = useRef(null);
  const Icon = section.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className="group"
    >
      <div className="flex gap-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors">
            <Icon className="w-6 h-6 text-cyan-400" />
          </div>
        </div>
        <div className="flex-1">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4">
            {section.title}
          </h2>
          <div className="text-slate-400 leading-relaxed whitespace-pre-line">
            {section.content}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
